<template>
  <SnackbarComponent
    v-model="snackbar.show"
    :message="snackbar.message"
    :color="snackbar.color"
  />
  <v-dialog v-model="internalValue" max-width="600">
    <v-card>
      <v-card-title class="py-2 px-4 text-subtitle-1">
        Table columns
      </v-card-title>
      <v-card-text class="pt-2 px-4 pb-3">
        <div class="text-caption text-medium-emphasis mb-3">
          Drag the handle to reorder columns. Use the checkbox to show or hide a
          column in the table.
        </div>

        <!-- Selected (default = true) columns -->
        <div class="section-header d-flex align-center mb-1">
          <span class="text-caption text-uppercase text-medium-emphasis">
            Visible columns
          </span>
          <v-spacer />
          <span class="text-caption text-disabled">
            {{ visibleHeaders.length }} selected
          </span>
        </div>

        <v-sheet class="settings-sheet mb-3" border rounded>
          <v-list dense class="settings-list">
            <v-list-item
              v-for="(h, index) in visibleHeaders"
              :key="`selected-${h.key || index}`"
              density="compact"
              draggable="true"
              @dragstart="onDragStart(index)"
              @dragover.prevent="onDragOver(index)"
              @drop="onDrop(index)"
            >
              <v-list-item-action class="d-flex align-center ga-2 w-100">
                <v-checkbox
                  :model-value="h.default"
                  @update:model-value="onToggleDefault(h, $event)"
                  :label="h.title"
                  class="flex-grow-1 text-body-2"
                  density="compact"
                  hide-details
                  :ripple="false"
                ></v-checkbox>
                <v-icon small class="drag-icon">mdi-drag</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-sheet>

        <!-- Unselected (default = false) columns -->
        <div class="section-header d-flex align-center mb-1 mt-2">
          <span class="text-caption text-uppercase text-medium-emphasis">
            Hidden columns
          </span>
          <v-spacer />
          <span class="text-caption text-disabled">
            {{ hiddenHeaders.length }} hidden
          </span>
        </div>

        <v-sheet class="settings-sheet" border rounded>
          <v-list dense class="settings-list">
            <v-list-item
              v-for="(h, index) in hiddenHeaders"
              :key="`hidden-${h.key || index}`"
              density="compact"
              draggable="true"
              @dragstart="onDragStart(index)"
              @dragover.prevent="onDragOver(index)"
              @drop="onDrop(index)"
            >
              <v-list-item-action class="d-flex align-center ga-2 w-100">
                <v-checkbox
                  :model-value="h.default"
                  @update:model-value="onToggleDefault(h, $event)"
                  :label="h.title"
                  class="flex-grow-1 text-body-2 text-medium-emphasis"
                  density="compact"
                  hide-details
                  :ripple="false"
                ></v-checkbox>
                <v-icon small class="drag-icon">mdi-drag</v-icon>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-sheet>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="onCancel">Cancel</v-btn>
        <v-btn color="primary" @click="onSave">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { generateClient } from "aws-amplify/api";
import { set_bms_global_settings } from "@/graphql/mutations";
import SnackbarComponent from "@/components/Extras/Snackbar.vue";

const client = generateClient();
// const LAST_REVIEWED_HEADER = {
//   title: "Last Reviewed",
//   key: "last_recommended_on",
//   sortable: true,
//   default: false, // hidden by default
// };
export default {
  name: "SettingsDialog",
  components: {
    SnackbarComponent,
  },
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    headers: {
      type: Array,
      default: () => [],
    },
    defaultHeaders: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["update:modelValue", "update:headers"],
  data() {
    return {
      localHeaders: [],
      draggedIndex: null,
      dragOverIndex: null,
      snackbar: {
        show: false,
        message: "",
        color: "success",
      },
    };
  },
  computed: {
    internalValue: {
      get() {
        return this.modelValue;
      },
      set(val) {
        this.$emit("update:modelValue", val);
      },
    },
    visibleHeaders() {
      return (this.localHeaders || []).filter((h) => h.default);
    },
    hiddenHeaders() {
      return (this.localHeaders || []).filter((h) => !h.default);
    },
  },
  watch: {
    modelValue(val) {
      if (val) {
        let cloned = this.headers
          ? JSON.parse(JSON.stringify(this.headers))
          : [];

        //   const exists = cloned.some(
        //     h => h.key === LAST_REVIEWED_HEADER.key
        //   );

        //   if (!exists) {
        //     cloned.push({ ...LAST_REVIEWED_HEADER });
        //   }

        this.localHeaders = cloned;
      }
    },
  },

  methods: {
    onDragStart(index) {
      this.draggedIndex = index;
    },
    onDragOver(index) {
      this.dragOverIndex = index;
    },
    onDrop(index) {
      if (this.draggedIndex === null) return;
      const arr = [...this.localHeaders];
      const [moved] = arr.splice(this.draggedIndex, 1);
      arr.splice(index, 0, moved);
      this.localHeaders = arr;
      this.draggedIndex = null;
      this.dragOverIndex = null;
    },
    onCancel() {
      this.$emit("update:modelValue", false);
    },
    onToggleDefault(header, value) {
      // Prevent unchecking the last visible column
      if (!value) {
        const visibleCount = (this.localHeaders || []).filter(
          (h) => h.default
        ).length;
        if (visibleCount <= 1) {
          return;
        }
      }
      header.default = value;
    },
    async onSave() {
      const updatedHeaders = JSON.parse(JSON.stringify(this.localHeaders));
      // console.log(updatedHeaders, "this is the updated headers");
      // this.$emit("update:headers", updatedHeaders);
      // this.$emit("update:modelValue", false);

      try {
        const response = await client.graphql({
          query: set_bms_global_settings,
          variables: {
            input: {
              bms_tab_configuration: JSON.stringify(updatedHeaders),
            },
          },
        });
        const responseData = JSON.parse(response.data.set_bms_global_settings);
        // console.log(responseData);
        if (responseData.status === "SUCCESS") {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message ||
              "Tabs configuration saved successfully!",
            color: "success",
          };
          this.$emit("update:modelValue", false);
          this.$emit("update:headers", "refresh");
        } else {
          this.snackbar = {
            show: true,
            message:
              responseData.status_message ||
              "Failed to save tabs configuration",
            color: "error",
          };
        }
      } catch (error) {
        console.error("Error saving tabs_configuration:", error);
        this.snackbar = {
          show: true,
          message:
            (error.errors && error.errors[0]?.message) ||
            error.message ||
            "An error occurred while saving tabs configuration",
          color: "error",
        };
      }
    },
  },
};
</script>

<style scoped>
.mb-2 {
  margin-bottom: 8px;
}

.settings-sheet {
  background-color: #fafafa;
}

.settings-list {
  max-height: 260px;
  overflow-y: auto;
}

.drag-icon {
  cursor: grab;
  color: rgba(0, 0, 0, 0.38);
}
</style>
